import React, { useState } from "react";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import styles from "./styles.module.scss";

// Colour the per-rule Status label by its last import outcome, bold so it reads
// against the dark greenish card. "Pending (first run)" and anything unexpected
// stay neutral (the panel's light text).
const STATUS_COLORS = {
    Healthy: "#3bd4ae", // textHighlight
    Failed: "#ff2e5b", // textError
};
const statusColor = (status) => STATUS_COLORS[status] || "#cae1da"; // textLight

// A run's raw status ("healthy"/"failed"/"running") shown capitalised.
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ISO instant -> "YYYY-MM-DD hh:mm PM KST". Times are shown in Korea time so the
// operator reads them in the market's timezone; a dash for a missing time and the
// raw value if it is not parseable.
const fmtTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Seoul",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: true,
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    const period = get("dayPeriod").replace(/\./g, "").toUpperCase();
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")} ${period} KST`;
};

// What each import supports today, straight from the market-data-stack template:
// its friendly label, the providers it accepts, and the markets each provider
// covers. An empty markets list means that provider takes no market. Enforced so
// a rule can only pick a valid source (and market) for its function.
//   - Stocks: FIS covers ASIA (one region); EOD_HISTORICAL covers the non-Asia
//     markets. KOSCOM (stocks) is retired.
//   - ETF constituent data is fetched on-demand (live per ETF at UI time via the
//     KOSCOM-ETF provider) and its sample data is seeded at deploy time, so there
//     is no scheduled ETF import: update-etf-details is not offered here.
//     Market-details/holidays come from FIS; currency from EOD; RIC from PermID;
//     reference data from Markit.
export const FUNCTION_SPEC = {
    "update-stocks": {
        label: "Import Stocks",
        providers: { FIS: ["ASIA"], EOD_HISTORICAL: ["NASDAQ", "HONGKONG", "AUSTRALIA"], SAMPLE: [] },
    },
    "update-currency": { label: "Import Currencies", providers: { EOD_HISTORICAL: [], SAMPLE: [] } },
    "update-market-details": { label: "Import Market Details", providers: { FIS: [], SAMPLE: [] } },
    "update-ric": { label: "Import RIC Codes", providers: { PERMID: [], SAMPLE: [] } },
    "import-ref-data": { label: "Import Reference Data", providers: { MARKIT: [], SAMPLE: [] } },
};

export const FUNCTIONS = Object.keys(FUNCTION_SPEC);
export const functionLabel = (fn) => FUNCTION_SPEC[fn]?.label || fn;
export const providersFor = (fn) => Object.keys(FUNCTION_SPEC[fn]?.providers || {});
export const marketsFor = (fn, provider) => FUNCTION_SPEC[fn]?.providers?.[provider] || [];

export const TIMEZONES = ["Asia/Seoul", "Asia/Tokyo", "UTC", "Europe/London", "America/New_York"];

export const ruleKey = (r) => r.id || r.__key;

// A valid starting draft: first function, its first provider, that provider's
// first market (if any).
export const defaultDraft = () => {
    const fn = FUNCTIONS[0];
    const provider = providersFor(fn)[0];
    const markets = marketsFor(fn, provider);
    return { function: fn, provider, market: markets[0] || "", runTimes: [], timezone: "Asia/Seoul" };
};

// A list of HH:MM time pickers with add / remove, replacing the old
// comma-separated free-text box.
const TimeList = ({ times, onChange, testIdPrefix }) => {
    const set = (i, val) => onChange(times.map((t, idx) => (idx === i ? val : t)));
    const add = () => onChange([...times, "09:00"]);
    const remove = (i) => onChange(times.filter((_, idx) => idx !== i));
    return (
        <div className={styles.timesList}>
            {times.length === 0 && <span className={styles.configHint}>No run times</span>}
            {times.map((t, i) => (
                <span key={i} className={styles.timeChip}>
                    <input
                        type="time"
                        className={styles.timeInput}
                        value={t}
                        onChange={(e) => set(i, e.target.value)}
                        data-testid={testIdPrefix ? `${testIdPrefix}-time-${i}` : undefined}
                    />
                    <button
                        type="button"
                        className={styles.removeTime}
                        onClick={() => remove(i)}
                        aria-label="Remove time"
                    >
                        ×
                    </button>
                </span>
            ))}
            <Button value="+ Add time" onClick={add} dataTestId={testIdPrefix ? `${testIdPrefix}-add-time` : undefined} />
        </div>
    );
};

/**
 * Operator-managed market-data import schedule: a list of rules, each firing its
 * function/provider/market at a set of daily times in a timezone. Presentational
 * only - pending edits live in the parent page and apply through its single
 * confirm/discard bar (no per-row save). The add form only offers valid
 * provider/market combinations for the chosen function.
 */
const MarketDataSchedule = ({ rules, statuses = {}, onViewHistory, onRunNow, onEditRule, onDeleteRule, draft, onDraftChange, onAddRule }) => {
    const draftProviders = providersFor(draft.function);
    const draftMarkets = marketsFor(draft.function, draft.provider);

    // Which rule's history panel is open, plus its loaded runs. Only one is open
    // at a time; clicking View History again closes it.
    const [openId, setOpenId] = useState(null);
    const [runs, setRuns] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    const toggleHistory = async (id) => {
        if (openId === id) {
            setOpenId(null);
            return;
        }
        setOpenId(id);
        setRuns([]);
        setHistoryError(null);
        setHistoryLoading(true);
        try {
            const res = await onViewHistory(id);
            setRuns(res?.runs || []);
        } catch (e) {
            setHistoryError(e.message || "Failed to load history");
        } finally {
            setHistoryLoading(false);
        }
    };

    // Changing the function resets provider + market to the first valid combo;
    // changing the provider resets the market. Keeps the draft always valid.
    const changeFunction = (fn) => {
        const provider = providersFor(fn)[0];
        const market = marketsFor(fn, provider)[0] || "";
        onDraftChange({ function: fn, provider, market });
    };
    const changeProvider = (provider) => {
        const market = marketsFor(draft.function, provider)[0] || "";
        onDraftChange({ provider, market });
    };

    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>Market Data schedule</div>
            <div className={styles.configForm}>
                {rules.length === 0 && <div className={styles.configHint}>No schedule rules yet.</div>}
                {rules.length > 0 && (
                <div className={styles.scheduleGrid}>
                {rules.map((r) => {
                    const key = ruleKey(r);
                    // Status + history come from server truth, keyed by the saved
                    // rule id; an unsaved (new) rule has neither yet.
                    const status = r.id ? statuses[r.id] : null;
                    const open = openId === r.id;
                    return (
                        <React.Fragment key={key}>
                        <div className={styles.scheduleRow}>
                            <span className={styles.scheduleId}>
                                {functionLabel(r.function)} · {r.provider}
                                {r.market ? " · " + r.market : ""}
                            </span>
                            <TimeList
                                times={r.runTimes || []}
                                onChange={(times) => onEditRule(key, { runTimes: times })}
                                testIdPrefix={"sched-" + key}
                            />
                            <select
                                className={styles.configSelect}
                                value={r.timezone || "Asia/Seoul"}
                                onChange={(e) => onEditRule(key, { timezone: e.target.value })}
                            >
                                {TIMEZONES.map((tz) => (
                                    <option key={tz} value={tz}>{tz}</option>
                                ))}
                            </select>
                            <CheckBox
                                name={"sched-en-" + key}
                                selected={!!r.enabled}
                                onChange={() => onEditRule(key, { enabled: !r.enabled })}
                                small
                                styleName="black"
                            >
                                <span className={styles.configHint}>Enabled</span>
                            </CheckBox>
                            <span className={styles.configHint} style={status ? { color: statusColor(status), fontWeight: 600 } : undefined}>
                                {status || ""}
                            </span>
                            {r.id ? (
                                <Button
                                    value={open ? "Hide History" : "View History"}
                                    onClick={() => toggleHistory(r.id)}
                                />
                            ) : (
                                <span />
                            )}
                            {r.id ? (
                                <Button value="Run Now" onClick={() => onRunNow(r)} />
                            ) : (
                                <span />
                            )}
                            <Button value="Delete" onClick={() => onDeleteRule(r)} />
                        </div>
                        {open && (
                            <div className={styles.historyPanel}>
                                {historyLoading && <div className={styles.configHint}>Loading...</div>}
                                {historyError && (
                                    <div className={styles.configHint} style={{ color: statusColor("Failed") }}>
                                        {historyError}
                                    </div>
                                )}
                                {!historyLoading && !historyError && runs.length === 0 && (
                                    <div className={styles.configHint}>No runs recorded yet.</div>
                                )}
                                {!historyLoading && runs.length > 0 && (
                                    <table className={styles.historyTable}>
                                        <thead>
                                            <tr>
                                                <th>Scheduled for</th>
                                                <th>Ran at</th>
                                                <th>Status</th>
                                                <th>Items</th>
                                                <th>Detail</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {runs.map((run, i) => (
                                                <tr key={i}>
                                                    <td>{fmtTime(run.scheduledFor)}</td>
                                                    <td>{fmtTime(run.runAt)}</td>
                                                    <td style={{ color: statusColor(cap(run.status)), fontWeight: 600 }}>
                                                        {cap(run.status)}
                                                    </td>
                                                    <td>{run.itemCount == null ? "-" : run.itemCount.toLocaleString()}</td>
                                                    <td className={styles.historyDetail}>{run.detail || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                        </React.Fragment>
                    );
                })}
                </div>
                )}
            </div>

            <div className={`${styles.configForm} ${styles.addRuleCard}`}>
                <div className={styles.addRuleLabel}>Add a rule</div>
                <div className={styles.scheduleAddRow}>
                    <select
                        className={styles.configSelect}
                        value={draft.function}
                        onChange={(e) => changeFunction(e.target.value)}
                        data-testid="sched-new-function"
                    >
                        {FUNCTIONS.map((f) => (
                            <option key={f} value={f}>{functionLabel(f)}</option>
                        ))}
                    </select>
                    <select
                        className={styles.configSelect}
                        value={draft.provider}
                        onChange={(e) => changeProvider(e.target.value)}
                        data-testid="sched-new-provider"
                    >
                        {draftProviders.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    {draftMarkets.length > 0 && (
                        <select
                            className={styles.configSelect}
                            value={draft.market}
                            onChange={(e) => onDraftChange({ market: e.target.value })}
                            data-testid="sched-new-market"
                        >
                            {draftMarkets.map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    )}
                    <TimeList
                        times={draft.runTimes || []}
                        onChange={(times) => onDraftChange({ runTimes: times })}
                        testIdPrefix="sched-new"
                    />
                    <select
                        className={styles.configSelect}
                        value={draft.timezone || "Asia/Seoul"}
                        onChange={(e) => onDraftChange({ timezone: e.target.value })}
                        data-testid="sched-new-timezone"
                    >
                        {TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>{tz}</option>
                        ))}
                    </select>
                    <Button value="Add rule" onClick={onAddRule} dataTestId="sched-add" />
                </div>
            </div>
        </div>
    );
};

export default MarketDataSchedule;
